import React, { useState, memo } from 'react';
import { Header } from '../components/Header';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Badge } from '../components/Badge';
import { SearchIcon, DownloadIcon, FilterIcon, CalendarIcon, XIcon, FileTextIcon, UserIcon, CheckCircleIcon, AlertTriangleIcon } from 'lucide-react';
interface ReviewScreenProps {
  onLogout: () => void;
  onNavigateMembers: () => void;
  onNavigateCases: () => void;
}
type DocType = 'Email' | 'Contract' | 'Memo' | 'Invoice' | 'Letter';
type DocStatus = 'Initial Review' | 'Enriched' | 'QA Flagged';
interface Document {
  id: string;
  date: string;
  type: DocType;
  status: DocStatus;
  summary: string;
  author?: string;
  confidence?: 'High' | 'Medium' | 'Low';
}
const MOCK_DOCS: Document[] = [{
  id: 'DOC-001042',
  date: '2023-08-14',
  type: 'Email',
  status: 'Enriched',
  summary: 'Discussion regarding Q3 supply chain delays and alternative vendors.',
  author: 'j.smith@acme.com',
  confidence: 'High'
}, {
  id: 'DOC-001043',
  date: '2023-08-15',
  type: 'Contract',
  status: 'Initial Review',
  summary: 'Master Service Agreement between Acme Corp and Global Logistics Inc.',
  author: 'Legal Dept',
  confidence: 'High'
}, {
  id: 'DOC-001044',
  date: '2023-08-15',
  type: 'Email',
  status: 'QA Flagged',
  summary: 'URGENT: Missing signatures on the Horizon addendum.',
  author: 'm.jones@acme.com',
  confidence: 'Medium'
}, {
  id: 'DOC-001045',
  date: '2023-08-18',
  type: 'Memo',
  status: 'Enriched',
  summary: 'Internal memo detailing the restructuring of the European division.',
  author: 'CEO Office',
  confidence: 'High'
}, {
  id: 'DOC-001046',
  date: '2023-08-20',
  type: 'Invoice',
  status: 'Initial Review',
  summary: 'Invoice #4492 from TechHoldings for Q3 consulting services.',
  author: 'Billing',
  confidence: 'High'
}, {
  id: 'DOC-001047',
  date: '2023-08-21',
  type: 'Email',
  status: 'Enriched',
  summary: 'Re: Invoice #4492 - Disputing hours billed for August.',
  author: 'finance@acme.com',
  confidence: 'High'
}, {
  id: 'DOC-001048',
  date: '2023-08-22',
  type: 'Letter',
  status: 'Initial Review',
  summary: 'Formal notice of breach of contract sent to Horizon Logistics.',
  author: 'Legal Dept',
  confidence: 'High'
}, {
  id: 'DOC-001049',
  date: '2023-08-25',
  type: 'Email',
  status: 'QA Flagged',
  summary: 'Fwd: Confidential - Project Phoenix timeline adjustments.',
  author: 'j.smith@acme.com',
  confidence: 'Low'
}, {
  id: 'DOC-001050',
  date: '2023-08-28',
  type: 'Contract',
  status: 'Enriched',
  summary: 'Non-Disclosure Agreement for prospective buyer (Redacted).',
  author: 'Legal Dept',
  confidence: 'High'
}, {
  id: 'DOC-001051',
  date: '2023-09-02',
  type: 'Memo',
  status: 'Initial Review',
  summary: 'Q4 Strategic Goals and hiring freeze announcement.',
  author: 'HR Dept',
  confidence: 'High'
}, {
  id: 'DOC-001052',
  date: '2023-09-05',
  type: 'Email',
  status: 'Enriched',
  summary: 'Approval for exception to hiring freeze for senior engineering role.',
  author: 'CEO Office',
  confidence: 'High'
}, {
  id: 'DOC-001053',
  date: '2023-09-10',
  type: 'Invoice',
  status: 'Initial Review',
  summary: 'Legal fees for August 2023 - Smith & Associates.',
  author: 'Billing',
  confidence: 'High'
}];
export function ReviewScreen({
  onLogout,
  onNavigateMembers,
  onNavigateCases
}: ReviewScreenProps) {
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const getTypeBadgeVariant = (type: DocType) => {
    switch (type) {
      case 'Email':
        return 'blue';
      case 'Contract':
        return 'green';
      case 'Memo':
        return 'grey';
      case 'Invoice':
        return 'amber';
      case 'Letter':
        return 'default';
      default:
        return 'default';
    }
  };
  const getStatusBadgeVariant = (status: DocStatus) => {
    switch (status) {
      case 'Initial Review':
        return 'grey';
      case 'Enriched':
        return 'teal';
      case 'QA Flagged':
        return 'amber';
      default:
        return 'default';
    }
  };
  return <div className="h-screen flex flex-col overflow-hidden" data-id="element-113">
      <Header caseName="Smith v. Acme Corp" documentCount={2847} onLogout={onLogout} onNavigateBack={onNavigateCases} data-id="element-114" />

      {/* Toolbar */}
      <div className="bg-navy-light border-b border-navy-lighter p-4 flex items-center gap-4 shrink-0" data-id="element-115">
        <div className="w-80" data-id="element-116">
          <Input placeholder="Search documents..." icon={<SearchIcon className="w-4 h-4" data-id="element-118" />} data-id="element-117" />
        </div>

        <div className="flex items-center gap-2" data-id="element-119">
          <button className="flex items-center gap-2 px-3 py-2 bg-navy border border-navy-lighter rounded-lg text-sm text-gray-300 hover:bg-navy-hover transition-colors" data-id="element-120">
            <FilterIcon className="w-4 h-4" data-id="element-121" />
            All Types
          </button>
          <button className="flex items-center gap-2 px-3 py-2 bg-navy border border-navy-lighter rounded-lg text-sm text-gray-300 hover:bg-navy-hover transition-colors" data-id="element-122">
            <CalendarIcon className="w-4 h-4" data-id="element-123" />
            Date Range
          </button>
        </div>

        <div className="flex-1 flex justify-end gap-3" data-id="element-124">
          <Button variant="ghost" onClick={onNavigateMembers} data-id="element-125">
            <UserIcon className="w-4 h-4 mr-2" data-id="element-126" />
            Members
          </Button>
          <Button variant="outline" className="gap-2" data-id="element-127">
            <DownloadIcon className="w-4 h-4" data-id="element-128" />
            Export to Excel
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden relative" data-id="element-129">
        {/* Table Area */}
        <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${selectedDoc ? 'pr-[400px]' : ''}`} data-id="element-130">
          {/* Table Header */}
          <div className="grid grid-cols-[100px_100px_100px_120px_1fr_80px] gap-4 px-6 py-3 border-b border-navy-lighter bg-navy text-xs font-semibold text-gray-400 uppercase tracking-wider shrink-0" data-id="element-131">
            <div data-id="element-132">Doc ID</div>
            <div data-id="element-133">Date</div>
            <div data-id="element-134">Type</div>
            <div data-id="element-135">Status</div>
            <div data-id="element-136">Summary</div>
            <div className="text-right" data-id="element-137">Action</div>
          </div>

          {/* Table Body */}
          <div className="flex-1 overflow-auto" data-id="element-138">
            {MOCK_DOCS.map(doc => <div key={doc.id} className={`
                  grid grid-cols-[100px_100px_100px_120px_1fr_80px] gap-4 px-6 py-3 
                  border-b border-navy-lighter items-center text-sm
                  hover:bg-navy-hover transition-colors cursor-pointer
                  ${selectedDoc?.id === doc.id ? 'bg-navy-hover border-l-2 border-l-amber pl-[22px]' : ''}
                `} onClick={() => setSelectedDoc(doc)} data-id="element-139">
                <div className="text-gray-400 font-mono text-xs" data-id="element-140">{doc.id}</div>
                <div className="text-gray-300" data-id="element-141">{doc.date}</div>
                <div data-id="element-142">
                  <Badge variant={getTypeBadgeVariant(doc.type)} data-id="element-143">
                    {doc.type}
                  </Badge>
                </div>
                <div data-id="element-144">
                  <Badge variant={getStatusBadgeVariant(doc.status)} data-id="element-145">
                    {doc.status}
                  </Badge>
                </div>
                <div className="text-gray-200 truncate pr-4" title={doc.summary} data-id="element-146">
                  {doc.summary}
                </div>
                <div className="text-right" data-id="element-147">
                  <Button variant="outline" size="sm" className="py-1 px-2 text-xs" onClick={e => {
                e.stopPropagation();
                setSelectedDoc(doc);
              }} data-id="element-148">
                    View
                  </Button>
                </div>
              </div>)}
          </div>

          {/* Footer */}
          <div className="border-t border-navy-lighter bg-navy-light px-6 py-3 text-xs text-gray-400 shrink-0" data-id="element-149">
            Showing 1–12 of 2,847 documents
          </div>
        </div>

        {/* Drawer / Split View */}
        {selectedDoc && <div className="absolute top-0 right-0 bottom-0 w-[400px] bg-navy-light border-l border-navy-lighter flex flex-col shadow-2xl animate-in slide-in-from-right-8 duration-200" data-id="element-150">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-4 border-b border-navy-lighter shrink-0" data-id="element-151">
              <h3 className="font-serif text-lg text-white" data-id="element-152">
                Document Details
              </h3>
              <button onClick={() => setSelectedDoc(null)} className="p-1 text-gray-400 hover:text-white hover:bg-navy rounded transition-colors" data-id="element-153">
                <XIcon className="w-5 h-5" data-id="element-154" />
              </button>
            </div>

            {/* Drawer Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-155">
              {/* Metadata Panel */}
              <div className="bg-navy rounded-lg border border-navy-lighter p-4 space-y-4" data-id="element-156">
                <div className="flex justify-between items-start" data-id="element-157">
                  <div data-id="element-158">
                    <div className="text-xs text-gray-500 mb-1" data-id="element-159">
                      Document ID
                    </div>
                    <div className="font-mono text-sm text-white" data-id="element-160">
                      {selectedDoc.id}
                    </div>
                  </div>
                  <Badge variant={getStatusBadgeVariant(selectedDoc.status)} data-id="element-161">
                    {selectedDoc.status}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-162">
                  <div data-id="element-163">
                    <div className="text-xs text-gray-500 mb-1" data-id="element-164">Date</div>
                    <div className="text-sm text-gray-300" data-id="element-165">
                      {selectedDoc.date}
                    </div>
                  </div>
                  <div data-id="element-166">
                    <div className="text-xs text-gray-500 mb-1" data-id="element-167">Type</div>
                    <Badge variant={getTypeBadgeVariant(selectedDoc.type)} data-id="element-168">
                      {selectedDoc.type}
                    </Badge>
                  </div>
                </div>

                {selectedDoc.author && <div data-id="element-169">
                    <div className="text-xs text-gray-500 mb-1" data-id="element-170">
                      Author / Source
                    </div>
                    <div className="text-sm text-gray-300" data-id="element-171">
                      {selectedDoc.author}
                    </div>
                  </div>}

                <div className="pt-3 border-t border-navy-lighter" data-id="element-172">
                  <div className="text-xs text-gray-500 mb-2" data-id="element-173">Summary</div>
                  <p className="text-sm text-gray-300 leading-relaxed" data-id="element-174">
                    {selectedDoc.summary}
                  </p>
                </div>

                {selectedDoc.confidence && <div className="pt-3 border-t border-navy-lighter flex items-center justify-between" data-id="element-175">
                    <div className="text-xs text-gray-500" data-id="element-176">AI Confidence</div>
                    <div className="flex items-center text-xs" data-id="element-177">
                      {selectedDoc.confidence === 'High' && <CheckCircleIcon className="w-3 h-3 text-green-500 mr-1" data-id="element-178" />}
                      {selectedDoc.confidence === 'Medium' && <AlertTriangleIcon className="w-3 h-3 text-amber mr-1" data-id="element-179" />}
                      {selectedDoc.confidence === 'Low' && <AlertTriangleIcon className="w-3 h-3 text-red-500 mr-1" data-id="element-180" />}
                      <span className={selectedDoc.confidence === 'High' ? 'text-green-400' : selectedDoc.confidence === 'Medium' ? 'text-amber' : 'text-red-400'} data-id="element-181">
                        {selectedDoc.confidence}
                      </span>
                    </div>
                  </div>}
              </div>

              {/* PDF Preview Placeholder */}
              <div className="bg-navy rounded-lg border border-navy-lighter flex flex-col h-[300px]" data-id="element-182">
                <div className="p-3 border-b border-navy-lighter flex items-center justify-between bg-navy-hover rounded-t-lg" data-id="element-183">
                  <div className="flex items-center text-sm text-gray-300" data-id="element-184">
                    <FileTextIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-185" />
                    Preview
                  </div>
                </div>
                <div className="flex-1 flex items-center justify-center flex-col text-gray-500 p-6 text-center" data-id="element-186">
                  <FileTextIcon className="w-12 h-12 mb-3 opacity-20" data-id="element-187" />
                  <p className="text-sm" data-id="element-188">PDF Preview Area</p>
                  <p className="text-xs mt-1 opacity-50" data-id="element-189">
                    Document viewer loads here in production
                  </p>
                </div>
              </div>

              {/* Audit Trail */}
              <div className="text-xs text-gray-500 flex items-center justify-center" data-id="element-190">
                Last accessed by Jane Doe on Oct 24, 2023 at 14:32 EST
              </div>
            </div>

            {/* Drawer Footer Actions */}
            <div className="p-4 border-t border-navy-lighter bg-navy-light shrink-0 flex gap-3" data-id="element-191">
              <Button fullWidth data-id="element-192">Open Original PDF</Button>
              <Button variant="secondary" className="px-3" data-id="element-193">
                <DownloadIcon className="w-4 h-4" data-id="element-194" />
              </Button>
            </div>
          </div>}
      </div>
    </div>;
}